import { decamelizeKeys } from 'humps';

import { apiClient } from '../apiClient';

export const PATHS = {
  // Catalog of all selectable conditions.
  HEALTH_CONDITIONS: '/api/v4/health_conditions/',
  // The user's own selected conditions — read (v4) and write (v3), both keyed
  // by `?user_urn=`. Mirrors Sparta's conditions api.
  USER_HEALTH_CONDITIONS_READ: '/api/v4/user_health_conditions/',
  USER_HEALTH_CONDITIONS_WRITE: '/api/v3/user_health_conditions/',
} as const;

// One condition in the catalog (camelized from the wire's `display_name`).
export type HealthCondition = {
  key: string;
  displayName: string;
};

type HealthConditionsResponse = {
  items: HealthCondition[];
};

// The user's saved conditions come back as a list of items keyed the same way;
// only `key` is needed to seed the form selection.
type UserHealthConditionsResponse = {
  items: { key: string }[];
};

export async function fetchHealthConditions(signal?: AbortSignal): Promise<HealthCondition[]> {
  const res = await apiClient.get<HealthConditionsResponse>(PATHS.HEALTH_CONDITIONS, { signal });
  return res.data?.items ?? [];
}

// The user's currently-selected condition keys (for prefill).
export async function fetchUserHealthConditions(
  userUrn: string,
  signal?: AbortSignal
): Promise<string[]> {
  const res = await apiClient.get<UserHealthConditionsResponse>(PATHS.USER_HEALTH_CONDITIONS_READ, {
    params: { user_urn: userUrn },
    signal,
  });
  return (res.data?.items ?? []).map((item) => item.key);
}

// Persist the user's selection. "None of these" is the empty array. Mirrors
// Sparta's patchHealthConditionsForUser: PATCH with `{ keys }`, `user_urn` in
// the query string.
export async function updateUserHealthConditions(userUrn: string, keys: string[]): Promise<void> {
  await apiClient.patch(PATHS.USER_HEALTH_CONDITIONS_WRITE, decamelizeKeys({ keys }), {
    params: { user_urn: userUrn },
  });
}
